import { Injectable } from '@angular/core';
import { ApiCallService } from 'src/app/modules/core/services/api/api-call.service';
import { getMoment } from 'src/app/modules/core/services/datetime.service';
import { saveAs } from 'file-saver';
import { IFile } from '../../interfaces/file';
import { IFrontDiagramSender } from '../../interfaces/front-diagram-sender';
import { excelExportDateFormat } from 'projects/common/src/app/services/utils/util.service';
import { LanguageService } from 'projects/common/src/app/services/parameters';

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  constructor(public apiCall: ApiCallService, public languageService: LanguageService) {}

  /**
   * Function to get diagram data
   */
  public getDiagramData(): Promise<any> {
    const path = 'front/diagram';
    return this.apiCall.get(path);
  }

  /**
   * Function to download file
   *
   * @param fileGuid Guid of file
   */
  public downloadFile(name: string, fileGuid: string): Promise<void> {
    return new Promise((resolve) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const applicationExtension = ['pdf'];
      const imageExtension = ['jpg', 'jpeg', 'png'];

      const metadataPath = `file/info/${fileGuid}`;
      const filePath = `file/${fileGuid}`;
      this.apiCall.get(metadataPath).then(
        (response) => {
          const metadata = response.data;

          this.apiCall.get(filePath, null, true).then((fileResponse) => {
            const blob = new Blob([fileResponse], {
              type: imageExtension.includes(metadata.extension) ? `image/${metadata.extension}` : 'application/pdf',
            });

            const datetime = getMoment(new Date(), 'YYYY-MM-DDTHH:mm:ss.SSS').format('YYYY-MM-DD');
            const filename = `${name}_${datetime}.${metadata.extension}`;
            const file = new File([blob], filename, {
              type: imageExtension.includes(metadata.extension) ? `image/${metadata.extension}` : 'application/pdf',
            });

            saveAs(file);
            resolve();
          });
        },
        (error) => {
          console.log(error);
        },
      );
    });
  }

  /**
   * Function to download file
   *
   * @param fileGuid Guid of file
   */
  public downloadFrontImage(fileGuid: string): Promise<Blob> {
    return new Promise((resolve) => {
      const metadataPath = `file/info/${fileGuid}`;
      const filePath = `file/${fileGuid}`;
      this.apiCall.get(metadataPath).then((response) => {
        const metadata = response.data;
        this.apiCall.get(filePath, null, true).then((fileResponse) => {
          const blob = new Blob([fileResponse], { type: `image/${metadata.extension}` });
          resolve(blob);
        });
      });
    });
  }

  /**
   * Function to get IFile object by File
   *
   * @param file File
   */
  public getFileContent(fileSource: File): Promise<IFile> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(fileSource);

      reader.onload = () => {
        const file: IFile = {
          content: String(reader.result).split(',')[1],
          contentType: fileSource.type,
          length: fileSource.size,
          name: fileSource.name,
        };
        resolve(file);
      };
    });
  }

  /**
   * Function to upload file
   */
  public uploadFile(fileSender: IFrontDiagramSender): Promise<any> {
    const path = 'front/diagram';
    return this.apiCall.post(path, fileSender);
  }

  /**
   * Download the selected APK app.
   *
   * @param appVersionRegisterId: Application version Id.
   * @param appName: Application name.
   */
  public async downloadAPK(appVersionRegisterId: { appVersionRegisterId: number }, appName: string): Promise<any> {
    const response = await this.apiCall.get('AppRegister/download', appVersionRegisterId, true);
    const blob = new Blob([response], { type: 'application/vnd.android.package-archive' });
    const file = new File([blob], `${appName.replace(' ', '-')}.apk`, { type: 'application/vnd.android.package-archive' });
    saveAs(file);
  }

  /**
   * Download excel file
   *
   */
  public async downloadExcel(url: string, params?: any): Promise<void> {
    const blobResponse = await this.apiCall.get(url, params, true);
    const startDate = excelExportDateFormat(params.startDate);
    const endDate = excelExportDateFormat(params.endDate);
    const blobFile = new Blob([blobResponse], { type: 'application/vnd.ms.excel' });
    const dateFileName = `${startDate} - ${endDate}`;
    const filename = `${this.languageService.message('fronts-report-excel-filename')} - ${dateFileName}`;
    const file = new File([blobFile], `${filename}.xlsx`, { type: 'application/vnd.ms.excel' });
    saveAs(file);
  }
}
